import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  VStack,
  HStack,
  Spinner,
  Text,
  Heading,
  Input,
  InputGroup,
  InputLeftElement,
  FormControl,
  FormLabel,
  Select,
  Textarea,
  Grid,
  GridItem,
  FormErrorMessage,
  FormHelperText,
  Divider,
  Checkbox,
  Avatar,
  Flex,
  Badge,
  Button,
  IconButton as ChakraIconButton,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  AlertDialog,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogBody,
  AlertDialogFooter,
} from "@chakra-ui/react";
import { useNavigate, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFileLines,
  faSearch,
  faEdit,
  faAddressCard,
  faLocationDot,
  faUser,
  faBuilding,
  faUserTie,
  faPlus,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import DataTable from "../AppDataTable";
import apiService from "../../services/ApiService";
import { useAuth } from "../../context/AuthContext";
import * as IconButton from "../../themes/IconButtonSet";
import { tableCustomStyles, StatusActive } from "../../themes/TableStyles";
import { StandardHeader } from "../../themes/StandardHeader";
import { resolveProfileImageSrc } from "../../utils/profileImage";

const FARMER_TYPE_INDIVIDUAL = "บุคคลธรรมดา";
const FARMER_TYPE_CORPORATE = "นิติบุคคล";

// ต้องตรงกับตัวเลือกในหน้า /registrationfarmer
const CORPORATE_TYPES = [
  "บริษัทจำกัด",
  "บริษัทมหาชนจำกัด",
  "ห้างหุ้นส่วนจำกัด",
  "ห้างหุ้นส่วนสามัญนิติบุคคล",
  "สหกรณ์",
  "วิสาหกิจชุมชน",
  "มูลนิธิ",
  "สมาคม",
  "อื่นๆ",
];

const EMPTY_FORM_DATA = {
  FarmerID: "",
  FirstName: "",
  LastName: "",
  FirstName2: "",
  LastName2: "",
  IDCard: "",
  Gender: "M",
  BirthDate: "",
  AddressNo: "",
  AddressMoo: "",
  AddressRoad: "",
  AddressSubdistrict: "",
  AddressDistrict: "",
  AddressProvince: "",
  AddressZipcode: "",
  FarmerType: FARMER_TYPE_INDIVIDUAL,
  CorporateType: "",
  CompanyBranch: "",
  Email: "",
  Phone: "",
  Address: "",
  ProfileImage: "",
  Remark: "",
  Status: "Active",
  Password: "",
  ConfirmPassword: "",
  RoleID: "",
};

// BirthDate จาก API มาเป็น ISO string แต่ <Input type="date"> ต้องการรูปแบบ YYYY-MM-DD
const formatDateForInput = (value) => {
  if (!value) return "";
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "" : date.toISOString().slice(0, 10);
};

const createEmptyContact = (id = 1, isPrimary = true) => ({
  id,
  ContactName: "",
  ContactPhone: "",
  ContactEmail: "",
  IsPrimary: isPrimary,
});

const FarmerUserManagement = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { sessionId } = useAuth();
  const [farmers, setFarmers] = useState([]);
  const [filteredFarmers, setFilteredFarmers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterFarmerType, setFilterFarmerType] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState(EMPTY_FORM_DATA);
  const [contacts, setContacts] = useState([createEmptyContact()]);
  const [isEditing, setIsEditing] = useState(false);
  const [errors, setErrors] = useState({});
  const [privacyChecked, setPrivacyChecked] = useState(false);
  const [isResetPasswordOpen, setIsResetPasswordOpen] = useState(false);
  const [resetPasswordData, setResetPasswordData] = useState({
    NewPassword: "",
    ConfirmPassword: "",
  });
  const [resetPasswordError, setResetPasswordError] = useState("");
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [deleteImpact, setDeleteImpact] = useState({ reservationCount: 0, canDelete: false });
  const [deleteAcknowledged, setDeleteAcknowledged] = useState(false);
  const deleteCancelRef = useRef();

  const isCorporate = formData.FarmerType === FARMER_TYPE_CORPORATE;
  const roleName =
    roles.find((role) => String(role.RoleID) === String(formData.RoleID))?.RoleName || "";

  useEffect(() => {
    if (!sessionId) {
      navigate("/login", { state: { from: location.pathname } });
    }
  }, [sessionId, navigate, location.pathname]);

  const columns = [
    {
      name: "รหัสเกษตรกร",
      selector: (row) => row.FarmerID,
      sortable: true,
      minWidth: "130px",
      wrap: true,
    },
    {
      name: "ชื่อ / ชื่อนิติบุคคล",
      selector: (row) => `${row.FirstName || ""} ${row.LastName || ""}`.trim() || "-",
      sortable: true,
      grow: 2,
      minWidth: "160px",
      wrap: true,
    },
    {
      name: "ประเภทเกษตรกร",
      cell: (row) => (
        <Badge
          colorScheme={row.FarmerType === FARMER_TYPE_CORPORATE ? "purple" : "teal"}
          variant="subtle"
          px={2}
          py={0.5}
          borderRadius="full"
          fontSize="xs"
          fontWeight="normal"
          whiteSpace="normal"
        >
          {row.FarmerType || FARMER_TYPE_INDIVIDUAL}
        </Badge>
      ),
      selector: (row) => row.FarmerType || "",
      sortable: true,
      minWidth: "140px",
      wrap: true,
      hide: "md",
    },
    {
      name: "สถานะ",
      selector: (row) => <StatusActive status={row.Status === "Active"} />,
      sortable: true,
      width: "110px",
    },
    {
      name: "การดำเนินการ",
      cell: (row) => (
        <IconButton.Edit
          icon={faEdit}
          onClick={() => handleEdit(row)}
          label="แก้ไข"
          size={{ base: "xs", md: "sm" }}
        />
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      width: "110px",
    },
  ];

  const fetchFarmers = async () => {
    setIsLoading(true);
    try {
      const response = await apiService({
        method: "POST",
        url: "/getFarmerList",
        data: {},
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        const data = response.farmerList || [];
        setFarmers(data);
        setFilteredFarmers(data);
      }
    } catch (error) {
      console.error("Error fetching farmers:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchRoles = async () => {
    try {
      const response = await apiService({
        method: "POST",
        url: "/getRolesList",
        data: {},
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        setRoles(response.RolesList || []);
      }
    } catch (error) {
      console.error("Error fetching roles:", error);
    }
  };

  useEffect(() => {
    if (sessionId) {
      fetchFarmers();
      fetchRoles();
    }
  }, [sessionId]);

  const handleSearch = (e) => {
    const value = e.target.value.toLowerCase();
    setSearchTerm(value);
    applyFilters(value, filterFarmerType, filterStatus);
  };

  const handleFarmerTypeFilter = (e) => {
    const value = e.target.value;
    setFilterFarmerType(value);
    applyFilters(searchTerm, value, filterStatus);
  };

  const handleStatusFilter = (e) => {
    const value = e.target.value;
    setFilterStatus(value);
    applyFilters(searchTerm, filterFarmerType, value);
  };

  const applyFilters = (search, farmerType, status) => {
    let filtered = farmers;

    if (search) {
      const searchTerms = search.split(" ").filter((term) => term.length > 0);
      filtered = filtered.filter((row) =>
        searchTerms.every((term) =>
          [
            row.FarmerID,
            row.FirstName,
            row.LastName,
            row.FarmerType,
            row.CorporateType,
            row.Status,
          ]
            .filter(Boolean)
            .some((field) => String(field).toLowerCase().includes(term))
        )
      );
    }

    if (farmerType) {
      filtered = filtered.filter((row) => row.FarmerType === farmerType);
    }

    if (status) {
      filtered = filtered.filter((row) => row.Status === status);
    }

    setFilteredFarmers(filtered);
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    setFilterFarmerType("");
    setFilterStatus("");
    setFilteredFarmers(farmers);
  };

  const handleAddNew = () => {
    setFormData(EMPTY_FORM_DATA);
    setContacts([createEmptyContact()]);
    setIsEditing(false);
    setShowForm(true);
    setErrors({});
    setPrivacyChecked(false);
  };

  const handleEdit = async (row) => {
    try {
      const response = await apiService({
        method: "POST",
        url: "/getFarmerByID",
        data: { FarmerID: row.FarmerID },
        sessionId: sessionId || "",
      });

      if (response.result === "success" && response.farmer) {
        const { Contacts, ...farmerData } = response.farmer;
        const userResponse = await apiService({
          method: "POST",
          url: "/getUserByProfileID",
          data: { ProfileID: row.FarmerID, ProfileType: "Farmer" },
          sessionId: sessionId || "",
        });

        const userData = userResponse.result === "success" && userResponse.user ? userResponse.user : {};

        setFormData({
          ...EMPTY_FORM_DATA,
          ...farmerData,
          FarmerType: farmerData.FarmerType || FARMER_TYPE_INDIVIDUAL,
          CorporateType: farmerData.CorporateType || "",
          CompanyBranch: farmerData.CompanyBranch || "",
          Status: farmerData.Status === "Active" ? "Active" : "Inactive",
          Password: "",
          ConfirmPassword: "",
          RoleID: userData.RoleID || "",
        });
        setContacts(
          Contacts && Contacts.length > 0
            ? Contacts.map((c, index) => ({
                id: c.id ?? index + 1,
                ContactName: c.ContactName || "",
                ContactPhone: c.ContactPhone || "",
                ContactEmail: c.ContactEmail || "",
                IsPrimary: !!c.IsPrimary,
              }))
            : [createEmptyContact()]
        );
        setIsEditing(true);
        setShowForm(true);
        setErrors({});
        setPrivacyChecked(true);
      } else {
        alert("ไม่พบข้อมูลเกษตรกร");
      }
    } catch (error) {
      console.error("Error fetching farmer data:", error);
      alert("เกิดข้อผิดพลาดในการดึงข้อมูลเกษตรกร");
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    validateField(name, value);
  };

  const handleFarmerTypeChange = (e) => {
    const value = e.target.value;
    setFormData((prev) => ({
      ...prev,
      FarmerType: value,
      // ล้างฟิลด์ที่ไม่ใช้ของอีกประเภท เพื่อไม่ให้ค้างค่าเดิมตอนบันทึก
      CorporateType: value === FARMER_TYPE_CORPORATE ? prev.CorporateType : "",
      CompanyBranch: value === FARMER_TYPE_CORPORATE ? prev.CompanyBranch : "",
      LastName: value === FARMER_TYPE_CORPORATE ? "" : prev.LastName,
      LastName2: value === FARMER_TYPE_CORPORATE ? "" : prev.LastName2,
      Gender: value === FARMER_TYPE_CORPORATE ? "" : prev.Gender || "M",
      BirthDate: value === FARMER_TYPE_CORPORATE ? "" : prev.BirthDate,
    }));
    setErrors((prev) => {
      const next = { ...prev };
      delete next.LastName;
      delete next.CorporateType;
      return next;
    });
    if (value !== FARMER_TYPE_CORPORATE) {
      setContacts([createEmptyContact()]);
    }
  };

  const addContact = () => {
    const nextId = contacts.length > 0 ? Math.max(...contacts.map((c) => c.id)) + 1 : 1;
    setContacts([...contacts, createEmptyContact(nextId, false)]);
  };

  const removeContact = (id) => {
    if (contacts.length <= 1) return;
    const remaining = contacts.filter((c) => c.id !== id);
    if (!remaining.some((c) => c.IsPrimary)) {
      remaining[0].IsPrimary = true;
    }
    setContacts(remaining);
  };

  const handleContactChange = (id, field, value) => {
    setContacts(contacts.map((c) => (c.id === id ? { ...c, [field]: value } : c)));
  };

  const setPrimaryContact = (id) => {
    setContacts(contacts.map((c) => ({ ...c, IsPrimary: c.id === id })));
  };

  const validateField = (name, value) => {
    let newErrors = { ...errors };
    switch (name) {
      case "FarmerID":
      case "IDCard":
        if (!value) {
          newErrors[name] = "กรุณากรอกข้อมูล";
        } else if (value.length > 30) {
          newErrors[name] = "ต้องไม่เกิน 30 ตัวอักษร";
        } else {
          delete newErrors[name];
        }
        break;
      case "FirstName":
      case "FarmerType":
      case "Phone":
        if (!value) {
          newErrors[name] = "กรุณากรอกข้อมูล";
        } else {
          delete newErrors[name];
        }
        break;
      case "LastName":
        // นิติบุคคลใช้ชื่อเดียว ไม่มีนามสกุล
        if (!value && !isCorporate) {
          newErrors[name] = "กรุณากรอกข้อมูล";
        } else {
          delete newErrors[name];
        }
        break;
      case "CorporateType":
        if (!value && isCorporate) {
          newErrors[name] = "กรุณาเลือกประเภทนิติบุคคล";
        } else {
          delete newErrors[name];
        }
        break;
      case "Email":
        if (!value) {
          newErrors[name] = "กรุณากรอกอีเมล";
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
          newErrors[name] = "รูปแบบอีเมลไม่ถูกต้อง";
        } else {
          delete newErrors[name];
        }
        break;
      case "Password":
        if (!isEditing && !value) {
          newErrors[name] = "กรุณากรอกรหัสผ่าน";
        } else if (value && value.length < 6) {
          newErrors[name] = "รหัสผ่านต้องมีอย่างน้อย 6 ตัวอักษร";
        } else {
          delete newErrors[name];
        }
        break;
      case "ConfirmPassword":
        if (!isEditing && !value) {
          newErrors[name] = "กรุณายืนยันรหัสผ่าน";
        } else if (value && value !== formData.Password) {
          newErrors[name] = "รหัสผ่านไม่ตรงกัน";
        } else {
          delete newErrors[name];
        }
        break;
      default:
        delete newErrors[name];
        break;
    }
    setErrors(newErrors);
  };

  const validContacts = contacts.filter(
    (c) =>
      c.ContactName.trim() !== "" &&
      (c.ContactPhone.trim() !== "" || c.ContactEmail.trim() !== "")
  );

  const isFormValid = () => {
    const baseValid =
      formData.FarmerID &&
      formData.FarmerID.length <= 30 &&
      formData.IDCard &&
      formData.IDCard.length <= 20 &&
      formData.FirstName &&
      formData.FarmerType &&
      formData.Email &&
      /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.Email) &&
      formData.Phone &&
      // รหัสผ่านกรอกเฉพาะตอนสร้างใหม่ การเปลี่ยนรหัสผ่านของเกษตรกรเดิมใช้ปุ่มรีเซ็ตรหัสผ่าน
      (isEditing ||
        (formData.Password &&
          formData.Password.length >= 6 &&
          formData.ConfirmPassword === formData.Password)) &&
      Object.keys(errors).length === 0 &&
      privacyChecked;

    if (!baseValid) return false;

    if (isCorporate) {
      return !!formData.CorporateType && validContacts.length > 0;
    }

    return !!formData.LastName;
  };

  const handleSave = async () => {
    if (!isFormValid()) {
      alert("กรุณากรอกข้อมูลที่จำเป็นให้ครบถ้วน");
      return;
    }
    setIsLoading(true);
    try {
      const apiUrl = isEditing ? "/updateFarmer" : "/registerFarmer";
      const { ProfileImage, ...farmerFields } = formData;
      const submitData = {
        // Farmer.Status เก็บเป็นข้อความ Active/Inactive ไม่ใช่ค่า boolean
        ...farmerFields,
        Status: formData.Status === "Active" ? "Active" : "Inactive",
        CreateBy: sessionId,
        UpdatedBy: sessionId,
      };

      if (!isEditing) {
        submitData.ProfileImage = "";
      }

      if (isCorporate) {
        submitData.LastName = "";
        submitData.Contacts = validContacts.map(({ id, ...rest }) => rest);
      } else {
        submitData.CorporateType = "";
        submitData.CompanyBranch = "";
      }

      const response = await apiService({
        method: "POST",
        url: apiUrl,
        data: submitData,
        sessionId: sessionId || "",
      });

      if (response.result === "success") {
        alert(isEditing ? "อัปเดตข้อมูลเกษตรกรสำเร็จ!" : "สร้างข้อมูลเกษตรกรสำเร็จ!");
        setShowForm(false);
        fetchFarmers();
      }
    } catch (error) {
      console.error("Error saving farmer:", error);
      alert("เกิดข้อผิดพลาดในการบันทึก: " + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setFormData(EMPTY_FORM_DATA);
    setContacts([createEmptyContact()]);
    setErrors({});
    setPrivacyChecked(false);
  };

  // ลบข้อมูลได้เฉพาะเกษตรกรที่ยังไม่มีรายการสั่งจอง ถ้ามีใบจองแล้วต้องปิดการใช้งานแทน
  // เพราะรหัสเกษตรกรถูกอ้างอิงอยู่ในใบจองวัคซีน
  const handleDelete = async () => {
    setIsLoading(true);
    try {
      const response = await apiService({
        method: "POST",
        url: "/deleteFarmerByID",
        data: { FarmerID: formData.FarmerID, CheckOnly: true },
        sessionId: sessionId || "",
      });

      if (response.result === "success") {
        setDeleteImpact({
          reservationCount: response.ReservationCount || 0,
          canDelete: response.CanDelete,
        });
        setDeleteAcknowledged(false);
        setIsDeleteDialogOpen(true);
      }
    } catch (error) {
      console.error("Error checking farmer references:", error);
      alert("ไม่สามารถตรวจสอบข้อมูลการใช้งานของเกษตรกรรายนี้ได้: " + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const confirmPermanentDelete = async () => {
    setIsLoading(true);
    try {
      const response = await apiService({
        method: "POST",
        url: "/deleteFarmerByID",
        data: { FarmerID: formData.FarmerID },
        sessionId: sessionId || "",
      });

      if (response.result === "success") {
        setIsDeleteDialogOpen(false);
        alert("ลบข้อมูลเกษตรกรเรียบร้อยแล้ว");
        setShowForm(false);
        fetchFarmers();
      }
    } catch (error) {
      console.error("Error deleting farmer:", error);
      alert("เกิดข้อผิดพลาดในการลบ: " + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const openResetPassword = () => {
    setResetPasswordData({ NewPassword: "", ConfirmPassword: "" });
    setResetPasswordError("");
    setIsResetPasswordOpen(true);
  };

  const handleResetPassword = async () => {
    if (resetPasswordData.NewPassword.length < 6) {
      setResetPasswordError("รหัสผ่านต้องมีอย่างน้อย 6 ตัวอักษร");
      return;
    }
    if (resetPasswordData.NewPassword !== resetPasswordData.ConfirmPassword) {
      setResetPasswordError("รหัสผ่านไม่ตรงกัน");
      return;
    }

    setIsLoading(true);
    try {
      const response = await apiService({
        method: "POST",
        url: "/resetUserPasswordByProfile",
        data: {
          ProfileID: formData.FarmerID,
          ProfileType: "Farmer",
          NewPassword: resetPasswordData.NewPassword,
        },
        sessionId: sessionId || "",
      });

      if (response.result === "success") {
        setIsResetPasswordOpen(false);
        alert("รีเซ็ตรหัสผ่านเรียบร้อยแล้ว กรุณาแจ้งรหัสผ่านใหม่ให้เกษตรกรทราบ");
      } else {
        setResetPasswordError(response.error || "ไม่สามารถรีเซ็ตรหัสผ่านได้");
      }
    } catch (error) {
      console.error("Error resetting password:", error);
      setResetPasswordError(error.message || "ไม่สามารถรีเซ็ตรหัสผ่านได้");
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleActive = () => {
    const nextActive = formData.Status !== "Active";
    const confirmMessage = nextActive
      ? "ยืนยันการเปิดการใช้งานเกษตรกรรายนี้ เกษตรกรจะกลับมาเข้าสู่ระบบและสั่งจองวัคซีนได้อีกครั้ง"
      : "ยืนยันการปิดการใช้งานเกษตรกรรายนี้ เกษตรกรจะไม่สามารถเข้าสู่ระบบได้ แต่ข้อมูลและใบจองเดิมจะยังอยู่ในระบบ";

    if (!window.confirm(confirmMessage)) return;
    setFarmerActiveStatus(nextActive);
  };

  const setFarmerActiveStatus = async (isActive) => {
    setIsLoading(true);
    try {
      const response = await apiService({
        method: "POST",
        url: "/setFarmerActiveStatus",
        data: { FarmerID: formData.FarmerID, Status: isActive },
        sessionId: sessionId || "",
      });

      if (response.result === "success") {
        setIsDeleteDialogOpen(false);
        alert(
          isActive
            ? "เปิดการใช้งานเกษตรกรสำเร็จ"
            : "ปิดการใช้งานเกษตรกรสำเร็จ เกษตรกรรายนี้จะไม่สามารถเข้าสู่ระบบได้ แต่ข้อมูลใบจองเดิมยังอยู่ในระบบ"
        );
        setShowForm(false);
        fetchFarmers();
      }
    } catch (error) {
      console.error("Error updating farmer status:", error);
      alert("เกิดข้อผิดพลาดในการปรับสถานะ: " + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  if (!sessionId) {
    return null;
  }

  return (
    <Box
      p={{ base: 3, sm: 4, md: 8, lg: 12 }}
      m={{ base: 2, md: 6, lg: 10 }}
      borderWidth={1}
      borderRadius="lg"
      boxShadow="lg"
    >
      <VStack spacing={{ base: 4, md: 6 }} align="stretch">
        <StandardHeader
          title="รายการข้อมูลเกษตรกร"
          bgColor="iBlue.600"
          icon={faFileLines}
        />

        {/* Modal สำหรับเพิ่ม/แก้ไขข้อมูลเกษตรกร */}
        <Modal
          isOpen={showForm}
          onClose={handleCancel}
          size={{ base: "full", md: "3xl", lg: "5xl", xl: "6xl" }}
          scrollBehavior="inside"
          isCentered
        >
          <ModalOverlay />
          <ModalContent maxH={{ base: "100vh", md: "90vh" }} my={{ base: 0, md: 4 }}>
            <ModalHeader
              bg="iBlue.600"
              color="white"
              borderTopRadius={{ base: 0, md: "md" }}
              py={{ base: 3, md: 4 }}
            >
              <Flex justify="space-between" align="center" pr={8} gap={4}>
                <Heading size={{ base: "sm", md: "md" }} fontWeight="normal">
                  {isEditing ? "แก้ไขข้อมูลเกษตรกร" : "เพิ่มข้อมูลเกษตรกร"}
                </Heading>
                {isEditing && (
                  <Box flexShrink={0} textAlign="center">
                    <Avatar
                      size={{ base: "md", md: "lg" }}
                      src={resolveProfileImageSrc(formData.ProfileImage)}
                      name={
                        !formData.ProfileImage
                          ? `${formData.FirstName || ""} ${formData.LastName || ""}`.trim()
                          : undefined
                      }
                      icon={<FontAwesomeIcon icon={faUser} />}
                    />
                  </Box>
                )}
              </Flex>
            </ModalHeader>
            <ModalCloseButton color="white" />
            <ModalBody py={{ base: 4, md: 6 }} px={{ base: 3, md: 6 }}>

            <Box borderWidth={1} borderRadius="lg" boxShadow="md" p={{ base: 3, md: 5 }} mb={{ base: 4, md: 6 }}>
              <StandardHeader
                title="ข้อมูลทั่วไป"
                bgColor="iTeal.500"
                icon={faAddressCard}
                mb={{ base: 4, md: 5 }}
              />
              <Grid
                templateColumns={{ base: "1fr", sm: "repeat(2, 1fr)", lg: "repeat(3, 1fr)" }}
                gap={{ base: 4, md: 6 }}
                mb={{ base: 4, md: 6 }}
              >
                <GridItem>
                  <FormControl isRequired isInvalid={!!errors.FarmerType}>
                    <FormLabel>ประเภทเกษตรกร</FormLabel>
                    <Select
                      name="FarmerType"
                      value={formData.FarmerType}
                      onChange={handleFarmerTypeChange}
                      colorScheme="iTeal"
                      icon={<FontAwesomeIcon icon={isCorporate ? faBuilding : faUser} />}
                    >
                      <option value={FARMER_TYPE_INDIVIDUAL}>{FARMER_TYPE_INDIVIDUAL}</option>
                      <option value={FARMER_TYPE_CORPORATE}>{FARMER_TYPE_CORPORATE}</option>
                    </Select>
                    <FormErrorMessage>{errors.FarmerType}</FormErrorMessage>
                  </FormControl>
                </GridItem>
                <GridItem>
                  <FormControl isRequired isInvalid={!!errors.FarmerID}>
                    <FormLabel>รหัสเกษตรกร</FormLabel>
                    <Input
                      name="FarmerID"
                      value={formData.FarmerID}
                      onChange={handleInputChange}
                      isDisabled={isEditing}
                      colorScheme="iTeal"
                    />
                    <FormErrorMessage>{errors.FarmerID}</FormErrorMessage>
                  </FormControl>
                </GridItem>
                <GridItem>
                  <FormControl isRequired isInvalid={!!errors.IDCard}>
                    <FormLabel>
                      {isCorporate ? "เลขทะเบียนนิติบุคคล" : "เลขบัตรประจำตัวประชาชน"}
                    </FormLabel>
                    <Input
                      name="IDCard"
                      value={formData.IDCard}
                      onChange={handleInputChange}
                      isDisabled={isEditing}
                      maxLength={13}
                      colorScheme="iTeal"
                    />
                    <FormErrorMessage>{errors.IDCard}</FormErrorMessage>
                  </FormControl>
                </GridItem>
              </Grid>

              <Divider mb={{ base: 4, md: 6 }} />

              {/* ฟิลด์เฉพาะบุคคลธรรมดา */}
              {!isCorporate && (
                <Grid templateColumns={{ base: "1fr", sm: "repeat(2, 1fr)", lg: "repeat(3, 1fr)" }} gap={{ base: 4, md: 6 }}>
                  <GridItem>
                    <FormControl isRequired isInvalid={!!errors.FirstName}>
                      <FormLabel>ชื่อ</FormLabel>
                      <Input
                        name="FirstName"
                        value={formData.FirstName}
                        onChange={handleInputChange}
                        colorScheme="iTeal"
                      />
                      <FormErrorMessage>{errors.FirstName}</FormErrorMessage>
                    </FormControl>
                  </GridItem>
                  <GridItem>
                    <FormControl isRequired isInvalid={!!errors.LastName}>
                      <FormLabel>นามสกุล</FormLabel>
                      <Input
                        name="LastName"
                        value={formData.LastName}
                        onChange={handleInputChange}
                        colorScheme="iTeal"
                      />
                      <FormErrorMessage>{errors.LastName}</FormErrorMessage>
                    </FormControl>
                  </GridItem>
                  <GridItem>
                    <FormControl>
                      <FormLabel>เพศ</FormLabel>
                      <Select
                        name="Gender"
                        value={formData.Gender || ""}
                        onChange={handleInputChange}
                        colorScheme="iTeal"
                      >
                        <option value="">-- เลือก --</option>
                        <option value="M">ชาย</option>
                        <option value="F">หญิง</option>
                      </Select>
                    </FormControl>
                  </GridItem>
                  <GridItem>
                    <FormControl>
                      <FormLabel>ชื่อ (ภาษาอื่น)</FormLabel>
                      <Input
                        name="FirstName2"
                        value={formData.FirstName2}
                        onChange={handleInputChange}
                        colorScheme="iTeal"
                      />
                    </FormControl>
                  </GridItem>
                  <GridItem>
                    <FormControl>
                      <FormLabel>นามสกุล (ภาษาอื่น)</FormLabel>
                      <Input
                        name="LastName2"
                        value={formData.LastName2}
                        onChange={handleInputChange}
                        colorScheme="iTeal"
                      />
                    </FormControl>
                  </GridItem>
                  <GridItem>
                    <FormControl>
                      <FormLabel>วันเกิด</FormLabel>
                      <Input
                        name="BirthDate"
                        type="date"
                        value={formatDateForInput(formData.BirthDate)}
                        onChange={handleInputChange}
                        colorScheme="iTeal"
                      />
                    </FormControl>
                  </GridItem>
                </Grid>
              )}

              {/* ฟิลด์เฉพาะนิติบุคคล */}
              {isCorporate && (
                <Grid templateColumns={{ base: "1fr", sm: "repeat(2, 1fr)", lg: "repeat(3, 1fr)" }} gap={{ base: 4, md: 6 }}>
                  <GridItem>
                    <FormControl isRequired isInvalid={!!errors.CorporateType}>
                      <FormLabel>ประเภทนิติบุคคล</FormLabel>
                      <Select
                        name="CorporateType"
                        value={formData.CorporateType}
                        onChange={handleInputChange}
                        placeholder="-- เลือกประเภทนิติบุคคล --"
                        colorScheme="iTeal"
                      >
                        {CORPORATE_TYPES.map((type) => (
                          <option key={type} value={type}>
                            {type}
                          </option>
                        ))}
                      </Select>
                      <FormErrorMessage>{errors.CorporateType}</FormErrorMessage>
                    </FormControl>
                  </GridItem>
                  <GridItem colSpan={{ base: 1, sm: 2, lg: 2 }}>
                    <FormControl isRequired isInvalid={!!errors.FirstName}>
                      <FormLabel>ชื่อนิติบุคคล</FormLabel>
                      <Input
                        name="FirstName"
                        value={formData.FirstName}
                        onChange={handleInputChange}
                        placeholder="ใส่ชื่อเต็ม เช่น บริษัท ทดสอบ จำกัด"
                        colorScheme="iTeal"
                      />
                      <FormHelperText>
                        กรุณาใส่ชื่อเต็มของนิติบุคคล เช่น บริษัท ทดสอบ จำกัด
                      </FormHelperText>
                      <FormErrorMessage>{errors.FirstName}</FormErrorMessage>
                    </FormControl>
                  </GridItem>
                  <GridItem>
                    <FormControl>
                      <FormLabel>สาขา</FormLabel>
                      <Input
                        name="CompanyBranch"
                        value={formData.CompanyBranch}
                        onChange={handleInputChange}
                        placeholder="สำนักงานใหญ่ / สาขาที่..."
                        colorScheme="iTeal"
                      />
                    </FormControl>
                  </GridItem>
                  <GridItem colSpan={{ base: 1, sm: 2, lg: 2 }}>
                    <FormControl>
                      <FormLabel>ชื่อนิติบุคคล (ภาษาอื่น)</FormLabel>
                      <Input
                        name="FirstName2"
                        value={formData.FirstName2}
                        onChange={handleInputChange}
                        colorScheme="iTeal"
                      />
                    </FormControl>
                  </GridItem>
                </Grid>
              )}
            </Box>

            {/* ผู้ติดต่อประสานงาน (เฉพาะนิติบุคคล) */}
            {isCorporate && (
              <Box borderWidth={1} borderRadius="lg" boxShadow="md" p={{ base: 3, md: 5 }} mb={{ base: 4, md: 6 }}>
                <StandardHeader
                  title="ผู้ติดต่อประสานงาน"
                  bgColor="iOrange.500"
                  icon={faUserTie}
                  mb={{ base: 4, md: 5 }}
                />
                <VStack spacing={4} align="stretch">
                  <Text color="gray.600" fontSize={{ base: "xs", md: "sm" }}>
                    ต้องมีผู้ติดต่ออย่างน้อย 1 คน (ระบุชื่อ และเบอร์โทรศัพท์หรืออีเมลอย่างน้อยหนึ่งอย่าง)
                  </Text>

                  {contacts.map((contact, index) => (
                    <Box
                      key={contact.id}
                      p={{ base: 3, md: 4 }}
                      borderWidth={1}
                      borderRadius="md"
                      borderColor={contact.IsPrimary ? "orange.300" : "gray.200"}
                      bg={contact.IsPrimary ? "orange.50" : "white"}
                    >
                      <Flex justify="space-between" align="center" mb={3} gap={2} wrap="wrap">
                        <Badge colorScheme={contact.IsPrimary ? "orange" : "gray"}>
                          {contact.IsPrimary ? "ผู้ติดต่อหลัก" : `ผู้ติดต่อ ${index + 1}`}
                        </Badge>
                        <HStack spacing={2}>
                          {!contact.IsPrimary && (
                            <Button
                              size="xs"
                              colorScheme="orange"
                              variant="outline"
                              onClick={() => setPrimaryContact(contact.id)}
                            >
                              ตั้งเป็นผู้ติดต่อหลัก
                            </Button>
                          )}
                          {contacts.length > 1 && (
                            <ChakraIconButton
                              aria-label="ลบผู้ติดต่อ"
                              icon={<FontAwesomeIcon icon={faTrash} />}
                              size="xs"
                              colorScheme="red"
                              variant="outline"
                              onClick={() => removeContact(contact.id)}
                            />
                          )}
                        </HStack>
                      </Flex>

                      <Grid templateColumns={{ base: "1fr", lg: "repeat(3, 1fr)" }} gap={4}>
                        <GridItem>
                          <FormControl isRequired={index === 0}>
                            <FormLabel fontSize="sm" color="gray.600">ชื่อ-นามสกุล</FormLabel>
                            <Input
                              size="sm"
                              value={contact.ContactName}
                              onChange={(e) => handleContactChange(contact.id, "ContactName", e.target.value)}
                              placeholder="ชื่อ-นามสกุล ผู้ติดต่อ"
                            />
                          </FormControl>
                        </GridItem>
                        <GridItem>
                          <FormControl>
                            <FormLabel fontSize="sm" color="gray.600">เบอร์โทรศัพท์</FormLabel>
                            <Input
                              size="sm"
                              value={contact.ContactPhone}
                              onChange={(e) => handleContactChange(contact.id, "ContactPhone", e.target.value)}
                              placeholder="0xx-xxx-xxxx"
                            />
                          </FormControl>
                        </GridItem>
                        <GridItem>
                          <FormControl>
                            <FormLabel fontSize="sm" color="gray.600">อีเมล</FormLabel>
                            <Input
                              size="sm"
                              type="email"
                              value={contact.ContactEmail}
                              onChange={(e) => handleContactChange(contact.id, "ContactEmail", e.target.value)}
                              placeholder="email@example.com"
                            />
                          </FormControl>
                        </GridItem>
                      </Grid>
                    </Box>
                  ))}

                  <Button
                    leftIcon={<FontAwesomeIcon icon={faPlus} />}
                    colorScheme="orange"
                    variant="outline"
                    onClick={addContact}
                    alignSelf="flex-start"
                    size="sm"
                  >
                    เพิ่มผู้ติดต่อ
                  </Button>
                </VStack>
              </Box>
            )}

            <Box borderWidth={1} borderRadius="lg" boxShadow="md" p={{ base: 3, md: 5 }} mb={{ base: 4, md: 6 }}>
              <StandardHeader
                title="ข้อมูลที่อยู่"
                bgColor="iBlue.500"
                icon={faLocationDot}
                mb={{ base: 4, md: 5 }}
              />
              <Grid templateColumns={{ base: "1fr", sm: "repeat(2, 1fr)", lg: "repeat(3, 1fr)" }} gap={{ base: 4, md: 6 }}>
                <GridItem>
                  <FormControl>
                    <FormLabel>บ้านเลขที่</FormLabel>
                    <Input
                      name="AddressNo"
                      value={formData.AddressNo}
                      onChange={handleInputChange}
                      colorScheme="iBlue"
                    />
                  </FormControl>
                </GridItem>
                <GridItem>
                  <FormControl>
                    <FormLabel>หมู่</FormLabel>
                    <Input
                      name="AddressMoo"
                      value={formData.AddressMoo}
                      onChange={handleInputChange}
                      colorScheme="iBlue"
                    />
                  </FormControl>
                </GridItem>
                <GridItem>
                  <FormControl>
                    <FormLabel>ถนน</FormLabel>
                    <Input
                      name="AddressRoad"
                      value={formData.AddressRoad}
                      onChange={handleInputChange}
                      colorScheme="iBlue"
                    />
                  </FormControl>
                </GridItem>
                <GridItem>
                  <FormControl>
                    <FormLabel>ตำบล</FormLabel>
                    <Input
                      name="AddressSubdistrict"
                      value={formData.AddressSubdistrict}
                      onChange={handleInputChange}
                      colorScheme="iBlue"
                    />
                  </FormControl>
                </GridItem>
                <GridItem>
                  <FormControl>
                    <FormLabel>อำเภอ</FormLabel>
                    <Input
                      name="AddressDistrict"
                      value={formData.AddressDistrict}
                      onChange={handleInputChange}
                      colorScheme="iBlue"
                    />
                  </FormControl>
                </GridItem>
                <GridItem>
                  <FormControl>
                    <FormLabel>จังหวัด</FormLabel>
                    <Input
                      name="AddressProvince"
                      value={formData.AddressProvince}
                      onChange={handleInputChange}
                      colorScheme="iBlue"
                    />
                  </FormControl>
                </GridItem>
                <GridItem>
                  <FormControl>
                    <FormLabel>รหัสไปรษณีย์</FormLabel>
                    <Input
                      name="AddressZipcode"
                      value={formData.AddressZipcode}
                      onChange={handleInputChange}
                      colorScheme="iBlue"
                    />
                  </FormControl>
                </GridItem>
                <GridItem colSpan={{ base: 1, sm: 2, lg: 3 }}>
                  <FormControl>
                    <FormLabel>ที่อยู่</FormLabel>
                    <Input
                      name="Address"
                      value={formData.Address}
                      onChange={handleInputChange}
                      colorScheme="iBlue"
                    />
                  </FormControl>
                </GridItem>
                <GridItem colSpan={{ base: 1, sm: 2, lg: 3 }}>
                  <FormControl>
                    <FormLabel>หมายเหตุ</FormLabel>
                    <Textarea
                      name="Remark"
                      value={formData.Remark}
                      onChange={handleInputChange}
                      colorScheme="iBlue"
                    />
                  </FormControl>
                </GridItem>
              </Grid>
            </Box>

            <Box borderWidth={1} borderRadius="lg" boxShadow="md" p={{ base: 3, md: 5 }} mb={{ base: 4, md: 6 }}>
              <StandardHeader
                title="ข้อมูลการติดต่อ"
                bgColor="iBlue.500"
                icon={faLocationDot}
                mb={{ base: 4, md: 5 }}
              />
              <Grid templateColumns={{ base: "1fr", sm: "repeat(2, 1fr)", lg: "repeat(3, 1fr)" }} gap={{ base: 4, md: 6 }}>
                <GridItem>
                  <FormControl isRequired isInvalid={!!errors.Email}>
                    <FormLabel>อีเมล</FormLabel>
                    <Input
                      name="Email"
                      value={formData.Email}
                      onChange={handleInputChange}
                      colorScheme="iBlue"
                    />
                    <FormErrorMessage>{errors.Email}</FormErrorMessage>
                  </FormControl>
                </GridItem>
                <GridItem>
                  <FormControl isRequired isInvalid={!!errors.Phone}>
                    <FormLabel>เบอร์โทรศัพท์</FormLabel>
                    <Input
                      name="Phone"
                      value={formData.Phone}
                      onChange={handleInputChange}
                      colorScheme="iBlue"
                    />
                    <FormErrorMessage>{errors.Phone}</FormErrorMessage>
                  </FormControl>
                </GridItem>
              </Grid>
            </Box>

            <Box borderWidth={1} borderRadius="lg" boxShadow="md" p={{ base: 3, md: 5 }} mb={{ base: 4, md: 6 }}>
              <StandardHeader
                title="ชื่อผู้ใช้และรหัสผ่าน"
                bgColor="iPurple.500"
                icon={faUser}
                mb={{ base: 4, md: 5 }}
              />
              <Grid templateColumns={{ base: "1fr", sm: "repeat(2, 1fr)", lg: "repeat(3, 1fr)" }} gap={{ base: 4, md: 6 }}>
                <GridItem>
                  <FormControl isRequired isInvalid={!!errors.Email}>
                    <FormLabel>ชื่อผู้ใช้ (อีเมล)</FormLabel>
                    <Input
                      name="Email"
                      value={formData.Email}
                      onChange={handleInputChange}
                      isDisabled
                      colorScheme="iPurple"
                    />
                    <FormErrorMessage>{errors.Email}</FormErrorMessage>
                  </FormControl>
                </GridItem>
                {isEditing ? (
                  <GridItem>
                    <FormControl>
                      <FormLabel>รหัสผ่าน</FormLabel>
                      <IconButton.Edit
                        onClick={openResetPassword}
                        label="รีเซ็ตรหัสผ่าน"
                        w={{ base: "100%", sm: "auto" }}
                      />
                      <FormHelperText>
                        การเปลี่ยนรหัสผ่านแยกออกจากการแก้ไขข้อมูลทั่วไป
                      </FormHelperText>
                    </FormControl>
                  </GridItem>
                ) : (
                  <>
                    <GridItem>
                      <FormControl isRequired isInvalid={!!errors.Password}>
                        <FormLabel>รหัสผ่าน</FormLabel>
                        <Input
                          name="Password"
                          type="password"
                          value={formData.Password}
                          onChange={handleInputChange}
                          colorScheme="iPurple"
                        />
                        <FormErrorMessage>{errors.Password}</FormErrorMessage>
                      </FormControl>
                    </GridItem>
                    <GridItem>
                      <FormControl isRequired isInvalid={!!errors.ConfirmPassword}>
                        <FormLabel>ยืนยันรหัสผ่าน</FormLabel>
                        <Input
                          name="ConfirmPassword"
                          type="password"
                          value={formData.ConfirmPassword}
                          onChange={handleInputChange}
                          colorScheme="iPurple"
                        />
                        <FormErrorMessage>{errors.ConfirmPassword}</FormErrorMessage>
                      </FormControl>
                    </GridItem>
                  </>
                )}
                <GridItem>
                  <FormControl>
                    <FormLabel>สิทธิ์การใช้งาน</FormLabel>
                    <Text minH="40px" display="flex" alignItems="center" fontWeight="medium">
                      {roleName || "-"}
                    </Text>
                    <FormHelperText>กำหนดโดยผู้ดูแลระบบ ไม่สามารถแก้ไขจากหน้านี้</FormHelperText>
                  </FormControl>
                </GridItem>
                <GridItem>
                  <FormControl>
                    <FormLabel>สถานะการใช้งาน</FormLabel>
                    <HStack spacing={3} align="center" minH="40px">
                      <StatusActive status={formData.Status === "Active"} />
                      <Text fontSize={{ base: "xs", md: "sm" }} color="gray.500">
                        {isEditing
                          ? "ปรับได้จากปุ่มปิด/เปิดการใช้งานด้านล่าง"
                          : "เกษตรกรที่สร้างใหม่จะเปิดการใช้งานทันที"}
                      </Text>
                    </HStack>
                  </FormControl>
                </GridItem>
              </Grid>
            </Box>

            <Box px={2}>
              <Checkbox
                isChecked={privacyChecked}
                onChange={(e) => setPrivacyChecked(e.target.checked)}
                size={{ base: "md", md: "lg" }}
                colorScheme="iOrange"
                alignItems="flex-start"
              >
                <Text fontSize={{ base: "xs", md: "md" }}>
                  ฉันได้อ่านนโยบายความเป็นส่วนตัวและเข้าใจนโยบายความเป็นส่วนตัวของเราแล้ว
                  และยอมรับเงื่อนไขการใช้งาน รวมถึงนโยบายความเป็นส่วนตัวของเรา
                  และยอมรับการใช้งานของคุกกี้ และเทคโนโลยีต่างๆ ที่เกี่ยวข้อง
                </Text>
              </Checkbox>
            </Box>
            </ModalBody>
            <ModalFooter gap={2} flexWrap="wrap" justifyContent="flex-end">
              <IconButton.Close
                onClick={handleCancel}
                label="ปิด"
                flex={{ base: "1 1 100%", sm: "0 0 auto" }}
              />
              {isEditing && (
                <>
                  {formData.Status === "Active" ? (
                    <IconButton.Cancel
                      onClick={handleToggleActive}
                      label="ปิดการใช้งาน"
                      flex={{ base: "1 1 100%", sm: "0 0 auto" }}
                    />
                  ) : (
                    <IconButton.Approved
                      onClick={handleToggleActive}
                      label="เปิดการใช้งาน"
                      flex={{ base: "1 1 100%", sm: "0 0 auto" }}
                    />
                  )}
                  <IconButton.Delete
                    onClick={handleDelete}
                    isLoading={isLoading}
                    label="ลบ"
                    flex={{ base: "1 1 100%", sm: "0 0 auto" }}
                  />
                </>
              )}
              <IconButton.Save
                onClick={handleSave}
                isLoading={isLoading}
                flex={{ base: "1 1 100%", sm: "0 0 auto" }}
              />
            </ModalFooter>
          </ModalContent>
        </Modal>

        {/* รีเซ็ตรหัสผ่าน แยกออกจากการแก้ไขข้อมูลทั่วไป */}
        <Modal
          isOpen={isResetPasswordOpen}
          onClose={() => setIsResetPasswordOpen(false)}
          size={{ base: "xs", sm: "md" }}
          isCentered
        >
          <ModalOverlay />
          <ModalContent>
            <ModalHeader bgColor="iPurple.500" color="white" fontSize={{ base: "md", md: "lg" }}>
              รีเซ็ตรหัสผ่าน
            </ModalHeader>
            <ModalCloseButton color="white" />
            <ModalBody py={{ base: 4, md: 6 }}>
              <VStack spacing={4} align="stretch">
                <Text fontSize={{ base: "sm", md: "md" }} color="gray.600">
                  กำหนดรหัสผ่านใหม่ให้ {formData.FirstName} {formData.LastName} (
                  {formData.Email})
                </Text>
                <FormControl isRequired>
                  <FormLabel>รหัสผ่านใหม่</FormLabel>
                  <Input
                    type="password"
                    value={resetPasswordData.NewPassword}
                    onChange={(e) => {
                      setResetPasswordError("");
                      setResetPasswordData({
                        ...resetPasswordData,
                        NewPassword: e.target.value,
                      });
                    }}
                    colorScheme="iPurple"
                  />
                  <FormHelperText>ต้องมีอย่างน้อย 6 ตัวอักษร</FormHelperText>
                </FormControl>
                <FormControl isRequired isInvalid={!!resetPasswordError}>
                  <FormLabel>ยืนยันรหัสผ่านใหม่</FormLabel>
                  <Input
                    type="password"
                    value={resetPasswordData.ConfirmPassword}
                    onChange={(e) => {
                      setResetPasswordError("");
                      setResetPasswordData({
                        ...resetPasswordData,
                        ConfirmPassword: e.target.value,
                      });
                    }}
                    colorScheme="iPurple"
                  />
                  <FormErrorMessage>{resetPasswordError}</FormErrorMessage>
                </FormControl>
              </VStack>
            </ModalBody>
            <ModalFooter gap={2} flexWrap="wrap" justifyContent="flex-end">
              <IconButton.Close
                onClick={() => setIsResetPasswordOpen(false)}
                label="ปิด"
                flex={{ base: "1 1 100%", sm: "0 0 auto" }}
              />
              <IconButton.Confirm
                onClick={handleResetPassword}
                isLoading={isLoading}
                label="บันทึกรหัสผ่านใหม่"
                flex={{ base: "1 1 100%", sm: "0 0 auto" }}
              />
            </ModalFooter>
          </ModalContent>
        </Modal>

        {/* ยืนยันการลบข้อมูลเกษตรกร หรือเสนอให้ปิดการใช้งานเมื่อมีใบจองอ้างอิงอยู่ */}
        <AlertDialog
          isOpen={isDeleteDialogOpen}
          leastDestructiveRef={deleteCancelRef}
          onClose={() => setIsDeleteDialogOpen(false)}
          isCentered
        >
          <AlertDialogOverlay>
            <AlertDialogContent mx={{ base: 4, md: 0 }}>
              <AlertDialogHeader fontSize={{ base: "md", md: "lg" }} fontWeight="bold">
                {deleteImpact.canDelete
                  ? "ยืนยันการลบข้อมูลเกษตรกร"
                  : "ไม่สามารถลบข้อมูลเกษตรกรได้"}
              </AlertDialogHeader>

              <AlertDialogBody fontSize={{ base: "sm", md: "md" }}>
                {deleteImpact.canDelete ? (
                  <VStack align="stretch" spacing={3}>
                    <Text>
                      เกษตรกรรายนี้ยังไม่มีรายการสั่งจองวัคซีน จึงสามารถลบข้อมูลออกจากระบบได้
                    </Text>
                    <Text color="gray.600">
                      ข้อมูลที่จะถูกลบ ได้แก่ ข้อมูลเกษตรกร ฟาร์ม ชนิดปศุสัตว์ ผู้ติดต่อประสานงาน
                      และบัญชีผู้ใช้สำหรับเข้าสู่ระบบ
                    </Text>
                    <Checkbox
                      isChecked={deleteAcknowledged}
                      onChange={(e) => setDeleteAcknowledged(e.target.checked)}
                      colorScheme="iOrange"
                      alignItems="flex-start"
                    >
                      <Text fontSize={{ base: "sm", md: "md" }}>
                        ฉันเข้าใจว่าข้อมูลจะถูกลบอย่างถาวรและไม่สามารถกู้คืนได้
                      </Text>
                    </Checkbox>
                  </VStack>
                ) : (
                  <VStack align="stretch" spacing={3}>
                    <Text>
                      เกษตรกรรายนี้มีรายการสั่งจองวัคซีนอยู่ในระบบแล้ว{" "}
                      {deleteImpact.reservationCount} รายการ
                      การลบข้อมูลจะทำให้ใบจองเดิมอ้างอิงถึงเกษตรกรไม่ได้
                    </Text>
                    <Text color="gray.600">
                      {formData.Status === "Active"
                        ? "แนะนำให้ปิดการใช้งานแทน เกษตรกรจะไม่สามารถเข้าสู่ระบบและสั่งจองวัคซีนใหม่ได้ แต่ข้อมูลและใบจองเดิมจะยังอยู่ในระบบ"
                        : "เกษตรกรรายนี้ถูกปิดการใช้งานอยู่แล้ว จึงไม่สามารถเข้าสู่ระบบหรือสั่งจองวัคซีนใหม่ได้"}
                    </Text>
                  </VStack>
                )}
              </AlertDialogBody>

              <AlertDialogFooter gap={2} flexWrap="wrap">
                <Button
                  ref={deleteCancelRef}
                  onClick={() => setIsDeleteDialogOpen(false)}
                  flex={{ base: "1 1 100%", sm: "0 0 auto" }}
                >
                  ยกเลิก
                </Button>
                {deleteImpact.canDelete ? (
                  <Button
                    colorScheme="deleteScheme"
                    leftIcon={<FontAwesomeIcon icon={faTrash} />}
                    onClick={confirmPermanentDelete}
                    isDisabled={!deleteAcknowledged}
                    isLoading={isLoading}
                    flex={{ base: "1 1 100%", sm: "0 0 auto" }}
                  >
                    ลบถาวร
                  </Button>
                ) : (
                  formData.Status === "Active" && (
                    <Button
                      colorScheme="cancelScheme"
                      onClick={() => setFarmerActiveStatus(false)}
                      isLoading={isLoading}
                      flex={{ base: "1 1 100%", sm: "0 0 auto" }}
                    >
                      ปิดการใช้งานแทน
                    </Button>
                  )
                )}
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialogOverlay>
        </AlertDialog>

        {/* ส่วนตัวกรอง */}
        <Flex
          w="100%"
          gap={3}
          direction={{ base: "column", lg: "row" }}
          align={{ base: "stretch", lg: "center" }}
          justify="space-between"
        >
          <Flex gap={3} wrap="wrap" align="center" flex="1" minW={0}>
            <InputGroup w={{ base: "100%", sm: "240px" }}>
              <InputLeftElement pointerEvents="none">
                <FontAwesomeIcon icon={faSearch} color="gray.300" />
              </InputLeftElement>
              <Input
                placeholder="ค้นหาเกษตรกร..."
                value={searchTerm}
                onChange={handleSearch}
                colorScheme="iBlue"
              />
            </InputGroup>
            <Select
              w={{ base: "100%", sm: "200px" }}
              value={filterFarmerType}
              onChange={handleFarmerTypeFilter}
              placeholder="เลือกประเภทเกษตรกร"
              colorScheme="iBlue"
            >
              <option value="">ทั้งหมด</option>
              <option value={FARMER_TYPE_INDIVIDUAL}>{FARMER_TYPE_INDIVIDUAL}</option>
              <option value={FARMER_TYPE_CORPORATE}>{FARMER_TYPE_CORPORATE}</option>
            </Select>
            <Select
              w={{ base: "100%", sm: "160px" }}
              value={filterStatus}
              onChange={handleStatusFilter}
              placeholder="เลือกสถานะ"
              colorScheme="iBlue"
            >
              <option value="">ทั้งหมด</option>
              <option value="Active">Active</option>
              <option value="Retired">Retired</option>
              <option value="Resigned">Resigned</option>
            </Select>
            <IconButton.Clear
              onClick={handleClearSearch}
              label="ล้าง"
              w={{ base: "100%", sm: "auto" }}
            />
          </Flex>
          {/*<IconButton.Add onClick={handleAddNew} label="เพิ่มข้อมูลใหม่" flexShrink={0} w={{ base: "100%", lg: "auto" }} />*/}
        </Flex>

        {isLoading ? (
          <Spinner size="md" />
        ) : farmers.length === 0 ? (
          <Text fontSize="lg" color="gray.500" textAlign="center" w="100%">
            ยังไม่มีข้อมูลเกษตรกร
          </Text>
        ) : filteredFarmers.length === 0 ? (
          <Text fontSize="lg" color="gray.500" textAlign="center" w="100%">
            ไม่พบข้อมูลที่ค้นหา
          </Text>
        ) : (
          <Box w="100%" overflowX="auto">
            <DataTable
              columns={columns}
              data={filteredFarmers}
              pagination
              highlightOnHover
              striped
              responsive
              customStyles={tableCustomStyles}
            />
          </Box>
        )}
      </VStack>
    </Box>
  );
};

export default FarmerUserManagement;